import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Subject } from 'rxjs';

import { LiveStageService } from '../../environment/LiveStage.service';
import { AdminTitle } from '../../layout/AdminTitle';
import { BaseLiveStageCrudList } from '../../crud/list/BaseLiveStageCrudList';
import { ICrudData } from 'tridion.web.ui/src/app/angular/crud/ICrudData';
import { LiveStage } from '../../environment/LiveStage';
import { AuthService } from 'tridion.web.ui/src/app/angular/login/adal/Auth.service';
import { ObjectIdCrud } from '../../crud/ObjectIdCrud';
import { PrettySubmitGuard } from 'tridion.web.ui/src/app/angular/save/PrettySubmitGuard';
import { TenaGDPRApi } from '../../api/product/tena/tcw/TenaGDPRApi';
import { ITenaUserData } from '../../model/product/tena/tcw/ITenaUserData';
import saveBlob from '../../api/fileManager/saveBlob';
import { AlertService } from 'tridion.web.ui/src/app/angular/layout/alert/Alert.service';

@Component({
    templateUrl: 'TenaGDPR.component.html',
    providers: [TenaGDPRApi]
})
export class TenaGDPRComponent
    extends BaseLiveStageCrudList<
        ITenaUserData,
        ICrudData<ITenaUserData>,
        { liveStage: LiveStage }
    >
    implements OnInit, OnDestroy {
    emailControl: FormControl;
    valForm: FormGroup;
    validEmails: string[] = [];
    showDelete = false;

    private unsubscribe: Subject<void> = new Subject();

    constructor(
        private adminTitle: AdminTitle,
        private authService: AuthService,
        elementRef: ElementRef,
        liveStageService: LiveStageService,
        prettySubmitGuard: PrettySubmitGuard,
        private tenaGDPRApi: TenaGDPRApi,
        private alertService: AlertService
    ) {
        super(elementRef, 'hms', liveStageService, prettySubmitGuard, 'GDPR');

        this.valForm = new FormGroup(
            {
                emailControl: new FormControl(
                    null,
                    Validators.compose([this.checkForm()])
                )
            },
            { updateOn: 'blur' }
        );

        this.emailControl = this.valForm.controls.emailControl as FormControl;
        this.emailControl.valueChanges.subscribe(value => {
            if (this.emailControl && this.emailControl.valid) {
                this.valForm.patchValue({ emailControl: null }, { emitEvent: false });
            }
        });
    }

    ngOnInit() {
        this.adminTitle.setTitle('GDPR - Tena');
        this.filter.observable.subscribe(() => {
            this.loadUserData();
        });
    }

    submitForm($ev: Event) {
        $ev.preventDefault();

        this.loadUserData();
    }

    validateForm(): boolean {
        this.emailControl.markAsTouched();

        return this.valForm.valid;
    }

    checkForm(): ValidatorFn {
        return (control: FormControl) => {
            if (!control.value && this.validEmails.length === 0) {
                return { required: true };
            }

            let blacklist = [];
            let invalidEmail = false;

            for (let email of this.getEmails(this.emailControl)) {
                let isValid = true;
                if (this.validEmails.indexOf(email) !== -1) {
                    blacklist.push(email);
                    isValid = false;
                }

                if (!this.isValidEmail(email)) {
                    invalidEmail = true;
                    isValid = false;
                }

                // Got this far -> valid email
                if (isValid && this.validEmails.indexOf(email) === -1) {
                    this.validEmails.push(email);
                }
            }
            let validation = {
                ...(blacklist.length > 0 && { blacklist: blacklist }),
                ...(invalidEmail && { invalidEmail: true })
            };

            return Object.keys(validation).length > 0 ? validation : null;
        };
    }

    getEmails(control: FormControl): string[] {
        let emails: string[] = [];
        if (control.value) {
            // Remove any white space and split on commas and semicolons
            emails = (control.value as string)
                .replace(/,/g, ';')
                .replace(/\s/g, '')
                .split(';');
        }

        return emails;
    }

    isValidEmail(email: string): boolean {
        let validEmailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

        return validEmailRegex.test(email);
    }

    forceSumbitForm() {
        this.emailControl.markAsDirty();
        this.emailControl.updateValueAndValidity();
        this.validateForm();
    }

    onKeydown(event: KeyboardEvent, value: string) {
        if (event.key === ',' || event.key === ';' || event.key === 'Enter') {
            this.emailControl.setValue(value);
            this.forceSumbitForm();

            // Prevent writing ",",";"
            if (event.key !== 'Enter') {
                event.preventDefault();
            }
        } else if (event.key === 'Backspace' && value === '') {
            this.validEmails.pop();
            this.forceSumbitForm();
        }
    }

    deleteNewRecepient(email: string) {
        this.validEmails.splice(this.validEmails.indexOf(email), 1);
        this.forceSumbitForm();
    }

    loadUserData() {
        if (this.emailControl.valid) {
            this.tenaGDPRApi.getUserData(this.validEmails).subscribe(
                data => {
                    this.clear();

                    this.init(data);

                    // Remove previous errors (server returns 404 for empty results, so we want to clear that if we do get results)
                    this.alertService.removeAll();
                },
                () => {
                    this.clear(); // Server returns 404 for empty result so clear results here.
                }
            );
        }
    }

    deleteAll() {
        if (this.validEmails && this.validEmails.length > 0) {
            this.tenaGDPRApi.deleteEmails(this.validEmails).subscribe(data => {
                this.clear();
                this.showDelete = false;
            });
        }
    }

    exportToCsv() {
        if (this.validEmails && this.validEmails.length > 0) {
            this.tenaGDPRApi.getCsv(this.validEmails).subscribe(response => {
                let fileName = this.validEmails.join(', ');

                saveBlob(response.text(), fileName, 'xls');
            });
        }
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
        super.ngOnDestroy();
    }

    protected createCrud() {
        return new ObjectIdCrud(this.tenaGDPRApi, this.authService);
    }
}
